import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { Phone, Mail, MapPin, Clock, Building } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    industry: '',
    service: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus({
        type: 'success',
        message: response.data.message,
      });
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        industry: '',
        service: '',
        message: '',
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to send your message. Please try again or contact us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'How quickly do you respond to inquiries?',
      answer: 'We aim to respond to all inquiries within 24 business hours. For urgent regulatory matters, please call us directly.',
    },
    {
      question: 'Do you work with international companies?',
      answer: 'Yes. Pegasus Clinicare regularly serves foreign companies seeking regulatory approval in India (CDSCO), as well as Indian companies seeking international market approvals (FDA, EU, etc.). We also provide US agent representation services for foreign establishments.',
    },
    {
      question: 'Do you offer free initial consultations?',
      answer: 'Yes. We offer a complimentary 30-minute regulatory consultation for new inquiries. Use the contact form or call us to schedule yours.',
    },
    {
      question: 'What industries do you serve?',
      answer: 'We serve pharmaceutical, medical device, cosmetic, food, nutraceutical, ayurvedic, and CRO/CMO sectors. If your industry is not listed, please reach out - we may still be able to help.',
    },
    {
      question: 'Do you offer retainer-based regulatory consulting?',
      answer: 'Yes. We offer project-based, retainer-based, and dedicated consulting engagement models to suit different business requirements and budget structures.',
    },
  ];

  return (
    <div className="pt-[72px]">
      <SEO page="contact" />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/djxr067u_07%20Contact%20us%2001%20.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #1570EF 0%, #1570EFee 35%, #1570EFaa 55%, transparent 75%)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white max-w-2xl" >Contact Us</h1>
          <p className="text-xl max-w-2xl text-white/90" >
            Let's Talk Regulatory Strategy - Reach Out to Our Expert Team Today.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="text-3xl font-bold mb-8" style={{ color: '#1B3A6B' }}>
                Get in Touch
              </h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center flex-shrink-0">
                    <Building size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#1B3A6B' }}>
                      Company
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      Pegasus Clinicare Pvt. Ltd.
                    </div>
                    <div className="text-sm" style={{ color: '#64748B' }}>
                      Directors: Pravin Jagtap | Jayesh Salunke
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#1B3A6B' }}>
                      Phone
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      <a href="tel:+919850271210" className="hover:text-[#1570EF]">
                        +91 9850271210 (Pravin Jagtap)
                      </a>
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      <a href="tel:+919158487862" className="hover:text-[#1570EF]">
                        +91 9158487862 (Jayesh Salunke)
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#1B3A6B' }}>
                      Email
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      <a href="mailto:pravin@pegasusclinicare.com / jayesh@pegasusclinicare.com" className="hover:text-[#1570EF]">
                        pravin@pegasusclinicare.com / jayesh@pegasusclinicare.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#1B3A6B' }}>
                      Location
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      Runwal Platinum, U 13, NDA Pashan Rd, Ram Nagar, Bavdhan, Pune, Maharashtra 411021
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#1B3A6B' }}>
                      Office Hours
                    </div>
                    <div className="text-base" style={{ color: '#1E293B' }}>
                      Monday - Saturday
                    </div>
                    <div className="text-sm" style={{ color: '#64748B' }}>
                      9:00 AM - 6:00 PM IST
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="rounded-2xl h-64 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.1224320555602!2d73.77831117462063!3d18.52336868257073!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bef7d8298b5f%3A0x47f54cc92637d6ec!2sRunwal%20platinum%2C%20U%2013%2C%20NDA%20Pashan%20Rd%2C%20Ram%20Nagar%2C%20Bavdhan%2C%20Pune%2C%20Maharashtra%20411021!5e0!3m2!1sen!2sin!4v1776709091986!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pegasus Clinicare Office Location"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-8" style={{ color: '#1B3A6B' }}>
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    data-testid="contact-form-company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-form-email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="contact-form-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Industry *
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    data-testid="contact-form-industry"
                    required
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  >
                    <option value="">Select Industry</option>
                    <option value="Pharma">Pharma</option>
                    <option value="Medical Devices">Medical Devices</option>
                    <option value="Cosmetics">Cosmetics</option>
                    <option value="Food & Nutraceuticals">Food & Nutraceuticals</option>
                    <option value="Ayurveda/Herbal">Ayurveda/Herbal</option>
                    <option value="CRO/CMO">CRO/CMO</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Service of Interest *
                  </label>
                  <select
                    id="service"
                    name="service"
                    data-testid="contact-form-service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200"
                  >
                    <option value="">Select Service</option>
                    <option value="Regulatory Affairs">Regulatory Affairs</option>
                    <option value="CDSCO Consulting">CDSCO Consulting</option>
                    <option value="FDA Consulting">FDA Consulting</option>
                    <option value="Revised Schedule M GMP">Revised Schedule M GMP</option>
                    <option value="Facility Design">Facility Design</option>
                    <option value="Turnkey Execution">Turnkey Execution</option>
                    <option value="Drug Sourcing">Drug Sourcing</option>
                    <option value="Cosmetic & Food Compliance">Cosmetic & Food Compliance</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-2" style={{ color: '#1B3A6B' }}>
                    Your Message / Requirement *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-form-message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#BFDBFE] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1570EF] transition-all duration-200 resize-none"
                  ></textarea>
                </div>

                {submitStatus && (
                  <div
                    data-testid="contact-form-status"
                    className={`p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={submitting}
                  className="w-full px-8 py-4 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="faq-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="FAQ"
            title="Frequently Asked Questions"
          />
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-item-${index}`}
                className="bg-white rounded-2xl border border-[#BFDBFE] overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-[#EFF6FF] transition-colors duration-200"
                >
                  <span className="font-semibold" style={{ color: '#1B3A6B' }}>
                    {faq.question}
                  </span>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="#1570EF"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-base" style={{ color: '#1E293B' }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
